import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PackageSearch } from "lucide-react";
import { toast } from "sonner";

const num = (v, d = 1) => (v === null || v === undefined ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));

function TargetDialog({ product, onClose }) {
  const qc = useQueryClient();
  const [t, setT] = useState(product.target_stock ?? "");
  const [l, setL] = useState(product.lead_time_days ?? "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/products/${product.product_id}/replenishment`, { target_stock: t === "" ? null : Number(t), lead_time_days: l === "" ? null : Number(l) });
      toast.success("Stock target saved"); qc.invalidateQueries({ queryKey: ["order-soon"] }); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm" data-testid="target-dialog">
        <DialogHeader><DialogTitle className="font-display">{product.name}</DialogTitle>
          <DialogDescription>Set the stock you aim to hold and the supplier lead time. Order Soon only uses these values.</DialogDescription></DialogHeader>
        <label className="text-xs text-slate-500">Stock target (units)<Input type="number" min="1" value={t} onChange={(e) => setT(e.target.value)} data-testid="target-input" className="mt-1" /></label>
        <label className="text-xs text-slate-500">Lead time (days, optional)<Input type="number" min="1" value={l} onChange={(e) => setL(e.target.value)} data-testid="lead-input" className="mt-1" /></label>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !(Number(t) > 0)} data-testid="target-save" className="bg-slate-900 text-white">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Table({ rows, kind, canEdit, onEdit }) {
  if (!rows.length) return <p className="py-10 text-center text-sm text-slate-500" data-testid={`os-empty-${kind}`}>No {kind === "customer" ? "customer-sale" : "internally used"} products are below the threshold.</p>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid={`os-table-${kind}`}>
        <thead className="bg-slate-50 text-xs text-slate-500"><tr>
          {["Product", "Stock", "Target", "Stock %", kind === "customer" ? "Sold (30d)" : "Used (30d)", "Avg / day", "Days left", "Suggested order", "Supplier", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>)}
        </tr></thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.product_id} data-testid={`os-row-${kind}-${r.product_id}`}>
              <td className="px-3 py-2"><div className="font-medium text-slate-900">{r.name}</div><div className="text-xs text-slate-400">{r.sku}</div>
                {r.movement === "no_recent_movement" && <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600" data-testid={`os-nomove-${r.product_id}`}>No recent movement</span>}
                {kind === "internal" && Object.keys(r.categories || {}).length > 0 && <div className="mt-1 text-[11px] text-slate-500">{Object.entries(r.categories).map(([k, v]) => `${k} ${num(v)}`).join(" · ")}</div>}
                {r.unknown_movements > 0 && <div className="text-[11px] text-amber-700">{r.unknown_movements} unrecognised movement(s) not counted</div>}</td>
              <td className="px-3 py-2 tabular">{num(r.stock)}</td>
              <td className="px-3 py-2 tabular">{num(r.target_stock)}</td>
              <td className="px-3 py-2 tabular font-semibold text-rose-600" data-testid={`os-pct-${kind}-${r.product_id}`}>{num(r.stock_pct)}%</td>
              <td className="px-3 py-2 tabular">{num(r.qty_30d)}</td>
              <td className="px-3 py-2 tabular">{r.avg_daily > 0 ? num(r.avg_daily, 2) : "—"}</td>
              <td className="px-3 py-2 tabular">{r.days_left ?? <span className="text-xs text-slate-400">Not enough data</span>}</td>
              <td className="px-3 py-2 tabular" data-testid={`os-suggest-${kind}-${r.product_id}`}>{num(r.suggested_qty, 0)}
                {r.suggestion_basis === "estimate" && <span className="ml-1 rounded bg-amber-50 px-1.5 text-[10px] text-amber-700">Estimate</span>}</td>
              <td className="px-3 py-2 text-xs text-slate-600">{r.supplier || "—"}</td>
              <td className="px-3 py-2">{canEdit && <button type="button" className="text-xs text-slate-500 underline" onClick={() => onEdit(r)} data-testid={`os-edit-${kind}-${r.product_id}`}>Edit target</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function OrderSoon() {
  const [sp, setSp] = useSearchParams();
  const tab = ["customer", "internal", "setup"].includes(sp.get("tab")) ? sp.get("tab") : "customer";
  const [edit, setEdit] = useState(null);
  const { data, isLoading, isError } = useQuery({ queryKey: ["order-soon"], queryFn: async () => (await api.get("/inventory/order-soon")).data, retry: false });
  if (isError) return <div className="p-6 text-sm text-slate-500" data-testid="order-soon-denied">You don't have access to inventory replenishment.</div>;
  if (isLoading || !data) return <div className="p-6 text-sm text-slate-400">Loading…</div>;
  const c = data.counts;
  return (
    <div className="space-y-5" data-testid="order-soon-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><PackageSearch className="h-6 w-6 text-pawkt-primary-deep" /> Order Soon</h1>
        <p className="mt-1 text-sm text-slate-500">Products whose physical stock is below {data.threshold_pct}% of their stock target. Rates use the last {data.window_days} days of the stock ledger; reservations are not tracked. Recommendations only — nothing is ordered or changed.</p>
      </div>
      {c.unknown_stock > 0 && <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="os-unknown">{c.unknown_stock} product(s) excluded: current stock does not match the stock ledger (shown as Unknown in Setup).</div>}
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v }, { replace: true })}>
        <TabsList>
          <TabsTrigger value="customer" data-testid="os-tab-customer">Customer sales ({c.customer})</TabsTrigger>
          <TabsTrigger value="internal" data-testid="os-tab-internal">Internal consumption ({c.internal})</TabsTrigger>
          <TabsTrigger value="setup" data-testid="os-tab-setup">Needs setup ({c.not_configured + c.unknown_stock})</TabsTrigger>
        </TabsList>
        <TabsContent value="customer"><Table rows={data.customer} kind="customer" canEdit={data.can_edit_targets} onEdit={setEdit} /></TabsContent>
        <TabsContent value="internal"><Table rows={data.internal} kind="internal" canEdit={data.can_edit_targets} onEdit={setEdit} /></TabsContent>
        <TabsContent value="setup">
          <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="os-setup">
            {data.unknown_stock.map((p) => <div key={p.product_id} className="flex justify-between px-4 py-2 text-sm" data-testid={`os-unknownrow-${p.product_id}`}><span>{p.name} <span className="text-xs text-slate-400">{p.sku}</span></span><span className="text-xs text-amber-700">Unknown — stock {num(p.stock)} vs ledger {num(p.ledger_balance)}</span></div>)}
            {data.not_configured.map((p) => (
              <div key={p.product_id} className="flex items-center justify-between px-4 py-2 text-sm" data-testid={`os-unconf-${p.product_id}`}>
                <span>{p.name} <span className="text-xs text-slate-400">{p.sku} · stock {num(p.stock)}</span></span>
                {data.can_edit_targets ? <button type="button" className="text-xs font-semibold text-pawkt-primary-deep underline" onClick={() => setEdit(p)} data-testid={`os-set-${p.product_id}`}>Set stock target</button>
                  : <span className="text-xs text-slate-400">Stock target not configured</span>}
              </div>
            ))}
            {!data.unknown_stock.length && !data.not_configured.length && <p className="py-8 text-center text-sm text-slate-500">Every product has a stock target.</p>}
          </div>
        </TabsContent>
      </Tabs>
      {edit && <TargetDialog product={edit} onClose={() => setEdit(null)} />}
    </div>
  );
}
